// src/utils/dateRangeUtils.ts

import { DateRangeOption, DateRangeOptionGroup, PlanIdentifier } from '../types/dateRange';

/**
 * 時間範圍解析、格式化、驗證工具函數
 *
 * Plan 和 Target Date 共用的工具函數庫
 *
 * ⚠️ 重要: 所有儲存到 Firestore 的值都是 YYYY-MM-DD 格式
 * 語義化標識 (如 "2025-W53") 僅用於 UI 顯示層
 */

// ============================================
// 日期計算工具函數
// ============================================

/**
 * 計算 ISO 8601 週數
 *
 * ISO 8601 規則:
 * - 週一為一週的開始
 * - 第一週是包含該年第一個週四的那一週
 *
 * @param date - 要計算週數的日期
 * @returns ISO 週數 (1-53)
 *
 * @example
 * getISOWeek(new Date('2025-12-26')) // 52
 * getISOWeek(new Date('2025-12-30')) // 1 (跨年週)
 */
export function getISOWeek(date: Date): number {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7; // 週一=0, 週日=6
  target.setDate(target.getDate() - dayNr + 3); // 移到該週的週四
  const firstThursday = target.valueOf();
  target.setMonth(0, 1); // 該年的 1 月 1 日
  if (target.getDay() !== 4) {
    // 移到第一週的週四
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
}

/**
 * 從 ISO 週數獲取該週的週一日期
 *
 * @param weekNum - ISO 週數 (1-53)
 * @param year - 年份
 * @returns 該週週一的日期
 *
 * @example
 * getWeekStartDate(52, 2025) // 2025-12-22 (Mon)
 * getWeekStartDate(1, 2026)  // 2025-12-29 (Mon, 跨年週)
 */
export function getWeekStartDate(weekNum: number, year: number): Date {
  // ISO 8601: 第一週包含該年第一個週四
  const jan4 = new Date(year, 0, 4); // 1月4日總是在第一週
  const jan4Day = jan4.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

  // 計算第一週的週一
  const firstMonday = new Date(jan4);
  const offset = jan4Day === 0 ? -6 : 1 - jan4Day; // 週日需要往前6天,其他日子往前到週一
  firstMonday.setDate(jan4.getDate() + offset);

  // 計算目標週的週一
  const targetMonday = new Date(firstMonday);
  targetMonday.setDate(firstMonday.getDate() + (weekNum - 1) * 7);

  return targetMonday;
}

/**
 * 從 ISO 週數獲取該週的週日日期
 *
 * @param weekNum - ISO 週數 (1-53)
 * @param year - 年份
 * @returns 該週週日的日期
 */
export function getWeekEndDate(weekNum: number, year: number): Date {
  const monday = getWeekStartDate(weekNum, year);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
}

/**
 * 獲取下一個週末 (週六) 的日期
 *
 * @param referenceDate - 參考日期,預設為今天
 * @returns 下一個週六的日期
 *
 * @example
 * // 假設今天是 2025-12-26 (週四)
 * getNextWeekend() // 2025-12-28 (本週六)
 *
 * // 假設今天是 2025-12-28 (週六)
 * getNextWeekend() // 2026-01-04 (下週六)
 */
export function getNextWeekend(referenceDate: Date = new Date()): Date {
  const date = new Date(referenceDate);
  date.setHours(0, 0, 0, 0); // 重置時間部分
  const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

  if (dayOfWeek === 6) {
    // 如果今天是週六,返回下週六
    date.setDate(date.getDate() + 7);
  } else {
    // 其他日子(包括週日),返回下一個週六
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    date.setDate(date.getDate() + (daysUntilSaturday === 0 ? 7 : daysUntilSaturday));
  }

  return date;
}

/**
 * 獲取下一個週一的日期
 *
 * @param referenceDate - 參考日期,預設為今天
 * @returns 下一個週一的日期
 *
 * @example
 * // 假設今天是 2025-12-26 (週四)
 * getNextMonday() // 2025-12-30 (下週一)
 *
 * // 假設今天是 2025-12-29 (週一)
 * getNextMonday() // 2026-01-05 (下下週一)
 */
export function getNextMonday(referenceDate: Date = new Date()): Date {
  const date = new Date(referenceDate);
  date.setHours(0, 0, 0, 0); // 重置時間部分
  const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

  if (dayOfWeek === 1) {
    // 如果今天是週一,返回下週一
    date.setDate(date.getDate() + 7);
  } else if (dayOfWeek === 0) {
    // 如果今天是週日,返回明天 (週一)
    date.setDate(date.getDate() + 1);
  } else {
    // 其他日子 (週二到週六),返回下週一
    const daysUntilNextMonday = (8 - dayOfWeek) % 7;
    date.setDate(date.getDate() + daysUntilNextMonday);
  }

  return date;
}

/**
 * 格式化日期為 YYYY-MM-DD 字串
 *
 * @param date - 要格式化的日期
 * @returns YYYY-MM-DD 格式字串
 *
 * @example
 * formatDateToString(new Date('2025-12-26')) // "2025-12-26"
 */
export function formatDateToString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 增加天數
 *
 * @param date - 基準日期
 * @param days - 要增加的天數
 * @returns 新日期
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

// ============================================
// Plan 專用 - 顯示文字轉換
// ============================================

/**
 * 將具體日期轉換為語義化顯示文字
 *
 * 轉換規則 (依優先順序):
 * 1. "Yesterday"           - 昨天
 * 2. "{n}d ago"            - 過去日期 (2天以上)
 * 3. "Today"               - 今天
 * 4. "Tomorrow"            - 明天
 * 5. "This {Weekday}"      - 本週內的其他日期 (如 "This Sunday")
 * 6. "Next Week"           - 下週 (週數差 = 1)
 * 7. "After {n} Weeks"     - n週後 (週數差 = 2, 3, 4...)
 * 8. "Next Month"          - 下個月任一天 (月份差距 = 1)
 * 9. "{Month Name}"        - 當年 + 遠期月份 (月份差距 ≥ 2)
 * 10. "{Month} {Year}"     - 跨年 + 遠期月份
 * 11. "{Month} {Day}"      - Default (所有其他情況)
 *
 * ⚠️ 週數優先於月份：即使跨月，週數邏輯優先
 *
 * @param planDate - Plan 日期 (YYYY-MM-DD 格式)
 * @param currentDate - 當前日期,預設為今天
 * @returns 語義化顯示文字
 *
 * @example
 * // 假設今天是 2025-12-26 (週五, Week 52)
 * formatPlanForDisplay("2025-12-25") // "Yesterday"
 * formatPlanForDisplay("2025-12-20") // "6d ago"
 * formatPlanForDisplay("2025-12-26") // "Today"
 * formatPlanForDisplay("2025-12-27") // "Tomorrow"
 * formatPlanForDisplay("2025-12-28") // "This Sunday"
 * formatPlanForDisplay("2025-12-30") // "Next Week" (下週二, Week 1)
 * formatPlanForDisplay("2026-01-05") // "After 2 Weeks" (Week 2)
 * formatPlanForDisplay("2026-01-15") // "Next Month" (週數差 > 4, 進入月份邏輯)
 * formatPlanForDisplay("2026-02-10") // "February"
 * formatPlanForDisplay("2027-03-10") // "March 2027"
 * formatPlanForDisplay("2026-06-15") // "Jun 15"
 */
export function formatPlanForDisplay(planDate: string, currentDate: Date = new Date()): string {
  const date = new Date(planDate);
  const current = new Date(currentDate);

  // 重置時間部分,只比較日期
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const currentOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());

  const daysDiff = Math.round((dateOnly.getTime() - currentOnly.getTime()) / (1000 * 60 * 60 * 24));

  // 規則 1: 昨天
  if (daysDiff === -1) {
    return "Yesterday";
  }

  // 規則 2: 過去日期 (2天以上)
  if (daysDiff < -1) {
    return `${Math.abs(daysDiff)}d ago`;
  }

  // 規則 3: 今天
  if (daysDiff === 0) {
    return "Today";
  }

  // 規則 4: 明天
  if (daysDiff === 1) {
    return "Tomorrow";
  }

  // 計算 ISO 週數
  const currentWeekNum = getISOWeek(current);
  const dateWeekNum = getISOWeek(date);

  // ISO 週數歸屬年份
  let currentIsoYear = current.getFullYear();
  if (currentWeekNum === 1 && current.getMonth() === 11) {
    currentIsoYear += 1;
  }

  let dateIsoYear = date.getFullYear();
  if (dateWeekNum === 1 && date.getMonth() === 11) {
    dateIsoYear += 1;
  }

  // 計算週數差 (相對於當前週)
  let weeksDiff: number;
  if (dateIsoYear === currentIsoYear) {
    weeksDiff = dateWeekNum - currentWeekNum;
  } else if (dateIsoYear === currentIsoYear + 1) {
    // 跨年情況: 計算剩餘週數
    const weeksInCurrentYear = getISOWeek(new Date(currentIsoYear - 1, 11, 28));
    weeksDiff = (weeksInCurrentYear - currentWeekNum) + dateWeekNum;
  } else {
    weeksDiff = Infinity; // 年份差距太大,不適用週數邏輯
  }

  // 規則 5: 本週內的其他日期 (未來, daysDiff > 1)
  if (weeksDiff === 0 && daysDiff > 1) {
    const weekdayName = date.toLocaleString('en-US', { weekday: 'long' });
    return `This ${weekdayName}`;
  }

  // 規則 6: 下週 (週數差 = 1)
  if (weeksDiff === 1) {
    return "Next Week";
  }

  // 規則 7: n週後 (週數差 = 2, 3, 4...)
  // 限制：只在合理範圍內使用週數邏輯 (例如 4 週內)
  if (weeksDiff >= 2 && weeksDiff <= 4) {
    return `After ${weeksDiff} Weeks`;
  }

  // 月份差異計算
  const monthsDiff = (date.getFullYear() - current.getFullYear()) * 12 + (date.getMonth() - current.getMonth());

  // 規則 8: 下個月任一天 (月份差距 = 1)
  if (monthsDiff === 1) {
    return "Next Month";
  }

  // 規則 9: 當年 + 遠期月份 (月份差距 ≥ 2)
  if (monthsDiff >= 2 && date.getFullYear() === current.getFullYear()) {
    return date.toLocaleString('en-US', { month: 'long' });
  }

  // 規則 10: 跨年 + 遠期月份
  if (monthsDiff >= 2 || date.getFullYear() > current.getFullYear()) {
    const monthName = date.toLocaleString('en-US', { month: 'long' });
    return `${monthName} ${date.getFullYear()}`;
  }

  // 規則 11: Default - 顯示具體日期
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * 日期緊迫程度類型
 */
export type DateUrgency = 'overdue' | 'urgent' | 'normal';

/**
 * 計算日期的緊迫程度
 *
 * @param dateString 日期字符串（YYYY-MM-DD 格式）
 * @param currentDate 當前日期（預設為今天）
 * @returns 緊迫程度：overdue（過期）、urgent（7天內）、normal（更遠）
 *
 * @example
 * getDateUrgency('2025-12-29') // 'overdue' (假設今天是 2025-12-30)
 * getDateUrgency('2025-12-31') // 'urgent' (1天後)
 * getDateUrgency('2026-01-05') // 'urgent' (6天後)
 * getDateUrgency('2026-01-15') // 'normal' (超過7天)
 */
export function getDateUrgency(
  dateString: string,
  currentDate: Date = new Date()
): DateUrgency {
  const date = new Date(dateString);
  const current = new Date(currentDate);

  // 重置時間部分，只比較日期
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const currentOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());

  const daysDiff = Math.round((dateOnly.getTime() - currentOnly.getTime()) / (1000 * 60 * 60 * 24));

  if (daysDiff < 0) return 'overdue';  // 過期
  if (daysDiff <= 7) return 'urgent';   // Today ~ Next Week（7天內）
  return 'normal';                      // 更遠的日期
}

/**
 * 從具體日期推斷語義化標識
 *
 * 推斷規則:
 * - 如果是週一 → ISO 週數標識 (YYYY-Www)
 * - 如果是月初(1-7號) → 月份標識 (YYYY-MM)
 * - 否則 → undefined (無法推斷)
 *
 * @param date - 具體日期 (YYYY-MM-DD 格式)
 * @returns 語義化標識或 undefined
 *
 * @example
 * inferSemanticPlan("2025-12-23") // "2025-W52" (週一)
 * inferSemanticPlan("2026-01-01") // "2026-01" (月初)
 * inferSemanticPlan("2026-01-15") // undefined (普通日期)
 */
export function inferSemanticPlan(date: string): string | undefined {
  const d = new Date(date);

  // 檢查是否為週一
  if (d.getDay() === 1) {
    const weekNum = getISOWeek(d);

    // ISO 週數歸屬年份的判斷:
    // - 如果週數是 1,且該日期在 12 月 → 歸屬於下一年
    // - 否則歸屬於該日期的年份
    let isoYear = d.getFullYear();
    if (weekNum === 1 && d.getMonth() === 11) {  // 11 = December
      isoYear += 1;
    }

    return `${isoYear}-W${weekNum.toString().padStart(2, '0')}`;
  }

  // 檢查是否為月初
  if (d.getDate() <= 7) {
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  return undefined;
}

// ============================================
// 選項生成
// ============================================

/**
 * 生成日期範圍選擇器選項
 *
 * @param enabledGroups - 啟用的選項組
 * @param referenceDate - 參考日期,預設為今天
 * @param forPlan - 是否為 Plan 模式 (決定 Quick 選項的生成邏輯)
 * @returns 選項陣列
 *
 * @example
 * // Plan 選擇器
 * generateDateRangeOptions(['quick', 'weeks', 'months'], new Date(), true)
 *
 * // Target Date 選擇器
 * generateDateRangeOptions(['quick', 'specific-date'], new Date(), false)
 */
export function generateDateRangeOptions(
  enabledGroups: DateRangeOptionGroup[],
  referenceDate: Date = new Date(),
  forPlan: boolean = true
): DateRangeOption[] {
  const options: DateRangeOption[] = [];

  // Quick 選項
  if (enabledGroups.includes('quick')) {
    options.push(...generateQuickOptions(forPlan, referenceDate));
  }

  // Weeks 選項 (Plan 專用)
  if (enabledGroups.includes('weeks')) {
    options.push(...generateWeeksOptions(referenceDate));
  }

  // Months 選項 (Plan 專用)
  if (enabledGroups.includes('months')) {
    options.push(...generateMonthsOptions(referenceDate));
  }

  return options;
}

/**
 * 生成快捷選項
 *
 * Plan 模式:
 * - Today
 * - Tomorrow
 * - Next Week (下週一)
 *
 * Target Date 模式:
 * - Today
 * - Tomorrow
 * - Weekend (本週六)
 * - Next Week (下週六)
 *
 * @param forPlan - 是否為 Plan 模式
 * @param referenceDate - 參考日期
 * @returns 快捷選項陣列
 */
function generateQuickOptions(forPlan: boolean, referenceDate: Date): DateRangeOption[] {
  const today = formatDateToString(referenceDate);
  const tomorrow = formatDateToString(addDays(referenceDate, 1));

  const options: DateRangeOption[] = [
    { value: today, label: "Today", group: "quick", isRecommended: true },
    { value: tomorrow, label: "Tomorrow", group: "quick", isRecommended: true }
  ];

  if (forPlan) {
    // Plan: Next Week = 下週一
    const nextMonday = getNextMonday(referenceDate);
    const nextMondayStr = formatDateToString(nextMonday);
    options.push({
      value: nextMondayStr,
      label: "Next Week",
      group: "quick",
      isRecommended: true,
      semanticValue: inferSemanticPlan(nextMondayStr) as PlanIdentifier | undefined  // 使用 inferSemanticPlan 確保正確處理跨年週數
    });
  } else {
    // Target Date: Weekend + Next Week (= Next Weekend)
    const thisWeekend = getNextWeekend(referenceDate);
    const nextWeekend = addDays(thisWeekend, 7);

    options.push(
      { value: formatDateToString(thisWeekend), label: "Weekend", group: "quick" },
      { value: formatDateToString(nextWeekend), label: "Next Week", group: "quick" }
    );
  }

  return options;
}

/**
 * 生成週數選項 (Plan 專用)
 *
 * 生成未來 4 週的選項:
 * - This Week (本週一)
 * - Next Week (下週一)
 * - Week 3, Week 4
 *
 * @param referenceDate - 參考日期
 * @returns 週數選項陣列
 */
function generateWeeksOptions(referenceDate: Date): DateRangeOption[] {
  const options: DateRangeOption[] = [];
  const currentWeekNum = getISOWeek(referenceDate);
  const currentYear = referenceDate.getFullYear();

  for (let i = 0; i < 4; i++) {
    let weekNum = currentWeekNum + i;
    let year = currentYear;

    // 處理跨年週數
    if (weekNum > 52) {
      // 注意: 有些年份有 53 週,這裡簡化處理
      // 實際應用中可能需要更精確的邏輯
      const weeksInYear = getISOWeek(new Date(year, 11, 28)); // 12月28日總是在最後一週
      if (weekNum > weeksInYear) {
        weekNum = weekNum - weeksInYear;
        year = year + 1;
      }
    }

    const monday = getWeekStartDate(weekNum, year);
    const mondayStr = formatDateToString(monday);
    const label = i === 0 ? "This Week" : i === 1 ? "Next Week" : `Week ${weekNum}`;

    options.push({
      value: mondayStr,
      label,
      group: "weeks",
      isRecommended: i < 2,
      semanticValue: inferSemanticPlan(mondayStr) as PlanIdentifier | undefined  // 使用 inferSemanticPlan 確保正確處理跨年週數
    });
  }

  return options;
}

/**
 * 生成月份選項 (Plan 專用)
 *
 * 生成未來 3 個月的選項:
 * - This Month (本月 1 號)
 * - Next Month (下月 1 號)
 * - Month 3
 *
 * @param referenceDate - 參考日期
 * @returns 月份選項陣列
 */
function generateMonthsOptions(referenceDate: Date): DateRangeOption[] {
  const options: DateRangeOption[] = [];

  for (let i = 0; i < 3; i++) {
    const targetDate = new Date(referenceDate);
    targetDate.setMonth(targetDate.getMonth() + i);
    targetDate.setDate(1); // 設為月初

    const year = targetDate.getFullYear();
    const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
    const monthName = targetDate.toLocaleString('en-US', { month: 'long' });

    const label = i === 0 ? "This Month" : monthName;

    options.push({
      value: formatDateToString(targetDate),
      label,
      group: "months",
      semanticValue: `${year}-${month}`
    });
  }

  return options;
}
